import { trigger, state, style, transition, animate } from '@angular/animations';
import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { EmailCampaignStatusHTMLSpanDraw, EmailHistoryStatus } from '@models/email/email-issue';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { EmailHistoryService } from '@services/email-history.service';
import { LoaderService } from '@services/loader.service';
import { AtlasGridOptions } from '@ui/components/atlas-grid/altas-grid-options.model';
import { AtlasGridComponent } from '@ui/components/atlas-grid/atlas-grid.component';
import { ButtonGroupRendererComponent } from '@ui/components/atlas-grid/renderer/buttongroup-renderer.component';
import { AtlasGridHtmlRendererComponent } from '@ui/components/atlas-grid/renderer/html-renderer.component';
import { formateDateTime } from '@utils/utils';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'wsb-email-history-running',
  templateUrl: './email-history-running.component.html',
  styleUrls: ['./email-history-running.component.scss'],
  animations: [
    trigger('gridIsLoaded', [
      state('hide', style({ opacity: 0 })),
      state('show', style({ opacity: 1 })),
      transition('hide => show', animate('150ms', style({ opacity: 1 }))),
      transition('show => hide', animate('150ms', style({ opacity: 0 })))
    ])
  ],
  standalone: false
})
export class EmailListRunningComponent implements OnInit, AfterViewInit {
  @ViewChild('grid') grid: AtlasGridComponent;
  @ViewChild('modalEmailPreview') modalEmailPreview;
  modalEmailPreviewModal: NgbModalRef;

  selectedEmailPreview = '';
  isLoaderActive = false;

  tableColumnsDefs;
  tableGridOptions;
  tableLoading: boolean;
  tableRowData = null;
  paginationPageSize = 10;
  gridAnimationState = 'hide';

  components = {
    buttonGroupRenderer: ButtonGroupRendererComponent,
    htmlRenderer: AtlasGridHtmlRendererComponent
  };
  constructor(
    private emailHistoryService: EmailHistoryService,
    private translateService: TranslateService,
    private loaderService: LoaderService,
    private modalService: NgbModal
  ) {}

  ngOnInit(): void {
    this.defineTable();

    this.gridOptionDefinition();
  }

  ngAfterViewInit(): void {
    this.grid.dataIsLoaded.subscribe(() => {
      this.gridAnimationState = 'show';
    });
  }

  defineTable() {
    this.tableColumnsDefs = [
      {
        headerNameTranslate: 'email_campaign_info',
        cellRenderer: 'htmlRenderer',
        cellRendererParams: {
          content: params => {
            if (params && params.data) {
              return this.campaignEmailHtml(params.data);
            }
            return '';
          }
        },
        sortable: false,
        onCellClicked: params => {
          this.openPreviewModal(params.data.idCampaignIssue);
        },
        cellStyle: { 'line-height': '25px' },
        width: 200
      },
      {
        headerNameTranslate: 'email_campaign_status',
        cellRenderer: 'htmlRenderer',
        cellRendererParams: {
          content: params => {
            return params && params.data ? this.getHtmlRenderEmailCampaignStatus(params.data) : '';
          }
        },
        suppressCellFocus: true,
        sortable: false,
        width: 120
      },
      {
        headerNameTranslate: 'email_campaign_recipients',
        field: 'countRecipients',
        width: 100,
        cellClass: ['campaign-email-recipients'],
        suppressCellFocus: true,
        sortable: false
      },
      // {
      //   headerNameTranslate: 'email_campaign_percentage',
      //   cellRenderer: 'htmlRenderer',
      //   cellRendererParams: {
      //     content: params => {
      //       if (!params || !params.data) {
      //         return '';
      //       }
      //       if (params.data.sentRecipients) {
      //         return (
      //           '<div class="d-flex flex-column"><span class="campaign-email-opens">' +
      //           params.data.opens +
      //           ' %</span><span>Percentage</span></div>'
      //         );
      //       }
      //       return '';
      //     }
      //   },
      //   suppressCellFocus: true,
      //   width: 100,
      //   cellStyle: { 'line-height': '25px' },
      //   cellClass: ['text-center'],
      //   sortable: false
      // },
      {
        headerNameTranslate: 'common_actions',
        suppressCellFocus: true,
        cellRenderer: 'buttonGroupRenderer',
        cellRendererParams: {
          items: [
            {
              // onClick: e => this.openPreviewModal(e.idCampaignIssue),
              label: 'common_copy'
            },
            {
              onClick: e => this.openPreviewModal(e.idCampaignIssue),
              label: 'mail_preview'
            },
            {
              // onClick: e => this.openPreviewModal(e.idCampaignIssue),
              label: 'common_edit'
            },
            {
              // onClick: e => this.openPreviewModal(e.idCampaignIssue),
              label: 'common_delete'
            }
          ]
        },

        cellClass: ['button-group', 'text-end'],
        maxWidth: 82,
        field: '__actions'
      }
    ];
  }

  gridOptionDefinition() {
    const that = this;
    this.tableGridOptions = AtlasGridOptions.deserialize({
      rowHeight: 110,
      pagination: true,
      // resizable: false,
      paginationPageSize: this.paginationPageSize,
      loadFunction: this.reloadList.bind(that),
      noRowsOverlayOptions: {
        message: ''
      }
    });
    this.reloadList();
  }

  reloadList(startRow?, endRow?, sort?) {
    if (!this.isLoaderActive) {
      this.loaderService.show();
      this.isLoaderActive = true;
    }
    return this.emailHistoryService
      .getSmsSentList(
        this.getFilterdStatus(),
        this.getExcludeStatus(),
        null,
        null,
        null,
        null,
        1 + startRow / (endRow - startRow),
        endRow - startRow,
        'lastSaveTime',
        'd'
      )
      .pipe(
        finalize(() => {
          this.loaderService.hide();
          this.isLoaderActive = false;
        })
      );
  }

  getFilterdStatus() {
    return [EmailHistoryStatus.RUNNING];
  }

  getExcludeStatus() {
    return [EmailHistoryStatus.ARCHIVED, EmailHistoryStatus.BLOCKED];
  }

  campaignEmailHtml(campaign) {
    return (
      '<div class="campaign-email-title">' +
      campaign.name +
      '</div>' +
      '<div class="campaign-email-subject">' +
      campaign.subject +
      '</div>' +
      this.getEditedOrSentHtml(campaign) +
      '</div>'
    );
  }

  getEditedOrSentHtml(emailCampaign) {
    const labels = ['mail_list_drafts_table_savedon', 'mail_send_campaign_input'];
    const edited = this.translateService.instant(labels[0]);
    const list = this.translateService.instant(labels[1]);
    if (emailCampaign.sendDate) {
      return (
        '<div class="campaign-email-editDate">' +
        '<span class="sendDate">' +
        edited +
        ': </span>' +
        (emailCampaign?.sendDate ? this.getFormattedDateHtml(emailCampaign.sendDate) : '') +
        '<span class="lastSaveTime"> ' +
        list +
        ': </span>' +
        (emailCampaign?.campaign?.title ? emailCampaign.campaign.title : '') +
        '</div>'
      );
    }
    return (
      '<div class="campaign-email-editDate">' +
      '<span class="sendDate">' +
      edited +
      ': </span>' +
      (emailCampaign?.lastSaveTime ? this.getFormattedDateHtml(emailCampaign.lastSaveTime) : ' -- ') +
      '<span class="lastSaveTime"> ' +
      list +
      ': </span>' +
      (emailCampaign?.campaign?.title ? emailCampaign.campaign.title : '') +
      '</div>'
    );
  }

  private getHtmlRenderEmailCampaignStatus(emailCampaign) {
    const status = emailCampaign.sendStatus;
    switch (status) {
      case EmailHistoryStatus.DRAFT:
        return this.getHtmlRenderStatus('mail_send_status_0', EmailCampaignStatusHTMLSpanDraw.DRAFT);
      case EmailHistoryStatus.SENT:
        return this.getHtmlRenderStatus('mail_send_status_2', EmailCampaignStatusHTMLSpanDraw.SENT);
      case EmailHistoryStatus.ERROR:
        return this.getHtmlRenderStatus('mail_send_status_5', EmailCampaignStatusHTMLSpanDraw.ERROR);
    }
  }

  private getHtmlRenderStatus(label, spanHTML) {
    const labelStatus = this.getTranslateLabels(label);
    return spanHTML + labelStatus + '</span>';
  }

  private getTranslateLabels(value: string) {
    if (value) {
      let labels;
      this.translateService.get(value).subscribe(label => {
        labels = label;
      });
      return labels;
    }
  }

  private getFormattedDateHtml(date) {
    const dateTime = formateDateTime(date).split(' ');
    const newDate = dateTime[0];
    const time = dateTime[2] ? dateTime[1] + ' ' + dateTime[2] : dateTime[1];
    return '<span>' + newDate + '</span> - <span>' + time + '</span>';
  }

  private openPreviewModal(emailCampaignId) {
    this.emailHistoryService.getEmailPreview(emailCampaignId).subscribe(issue => {
      this.selectedEmailPreview = issue.htmlBody;
      this.modalEmailPreviewModal = this.modalService.open(this.modalEmailPreview, { size: 'xl' });

      this.modalEmailPreviewModal.result.then(
        result => {},
        reason => {}
      );
    });
  }
}
